import React, { useEffect, useRef, useState } from "react"
import "./FormList.scss"
import { useNavigate } from "react-router-dom"
import FormApi from "../../../Api/FormApi"
import {
  Button,
  Col,
  Row,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  Card,
  CardBody,
  Badge,
  CardHeader,
  CardTitle,
  CardText,
  InputGroup,
  InputGroupText,
} from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import DataTableLoader from "SkeletonLoader/DataTableLoader"
import { Toast } from "primereact/toast"
import { ActionIcon } from "components/Common"
import { ConfirmDialog } from "primereact/confirmdialog"

const FormList = () => {
  document.title = "Forms"
  const navigate = useNavigate()
  const toast = useRef(null)
  const accessToken = localStorage.getItem("accessToken")

  const [forms, setForms] = useState([])
  const [filteredForms, setFilteredForms] = useState([])
  const [institutions, setInstitutions] = useState([])
  const [selectedInstitution, setSelectedInstitution] = useState("")
  const [selectedForm, setSelectedForm] = useState(null)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false)
  const [formToDelete, setFormToDelete] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [viewMode, setViewMode] = useState("cards") // "cards" or "table"

  const breadcrumbItems = [
    { label: "Pixwik", link: "/" },
    { label: "Forms", link: "/forms" },
    { label: "Form List" },
  ]

  const handleBreadcrumbClick = link => {
    if (link !== "#") {
      navigate(link)
    }
  }

  // Get institutions for dropdown
  const getInstitutions = () => {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    FormApi.getInstitutionMenuList(config)
      .then(response => {
        if (response.data.status && response.data.institutions) {
          // Create a copy and sort institutions alphabetically by displayName or businessName
          const institutionsCopy = [...response.data.institutions]
          const sortedInstitutions = institutionsCopy.sort((a, b) => {
            const nameA = (a.displayName || a.businessName || "").toLowerCase().trim()
            const nameB = (b.displayName || b.businessName || "").toLowerCase().trim()
            
            // Ensure proper alphabetical order (A to Z)
            if (nameA < nameB) return -1
            if (nameA > nameB) return 1
            return 0
          })
          
          console.log("Original institutions:", response.data.institutions.slice(0, 3).map(inst => inst.displayName || inst.businessName))
          console.log("Sorted institutions:", sortedInstitutions.slice(0, 3).map(inst => inst.displayName || inst.businessName))
          
          setInstitutions(sortedInstitutions)
        } else {
          setInstitutions([])
        }
      })
      .catch(error => {
        console.error("Error fetching institutions:", error)
        setInstitutions([])
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch institutions"
        })
      })
  }

  // Get forms for selected institution
  const getForms = (institutionId) => {
    if (!institutionId) {
      setForms([])
      setFilteredForms([])
      setLoading(false)
      return
    }

    setLoading(true)
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: {
        institutionId
      }
    }

    FormApi.getInstitutionForms(config)
      .then(response => {
        setLoading(false)
        if (response.data.status) {
          setForms(response.data.forms)
          setFilteredForms(response.data.forms)
        }
      })
      .catch(error => {
        console.error("Error fetching forms:", error)
        setLoading(false)
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error.response?.data?.message || "Failed to fetch forms"
        })
      })
  }

  useEffect(() => {
    getInstitutions()
  }, [])

  useEffect(() => {
    if (selectedInstitution) {
      getForms(selectedInstitution)
    } else {
      setForms([])
      setFilteredForms([])
    }
  }, [selectedInstitution])

  const handleInstitutionChange = (e) => {
    setSelectedInstitution(e.target.value)
    
    // Smooth scroll to prevent jarring jump when content appears
    if (e.target.value) {
      setTimeout(() => {
        const filterCard = document.querySelector('.filters-container')
        if (filterCard) {
          filterCard.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start',
            inline: 'nearest'
          })
        }
      }, 100)
    }
  }

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue)
    if (!searchValue.trim()) {
      setFilteredForms(forms)
    } else {
      const filtered = forms.filter(form =>
        form.formName.toLowerCase().includes(searchValue.toLowerCase()) ||
        form.formSlug?.toLowerCase().includes(searchValue.toLowerCase()) ||
        form.programId?.programName?.toLowerCase().includes(searchValue.toLowerCase()) ||
        form.courseId?.courseName?.toLowerCase().includes(searchValue.toLowerCase())
      )
      setFilteredForms(filtered)
    }
  }

  // Update filtered forms when forms change
  useEffect(() => {
    handleSearch(searchTerm)
  }, [forms])

  const getFormStats = () => {
    const totalForms = forms.length
    const totalFields = forms.reduce((sum, form) => sum + (form.fields?.length || 0), 0)
    const avgFieldsPerForm = totalForms > 0 ? Math.round(totalFields / totalForms) : 0
    const recentForms = forms.filter(form => {
      const createdDate = new Date(form.createdAt)
      const weekAgo = new Date()
      weekAgo.setDate(weekAgo.getDate() - 7)
      return createdDate > weekAgo
    }).length

    return { totalForms, totalFields, avgFieldsPerForm, recentForms }
  }

  const handleViewForm = (form) => {
    setSelectedForm(form)
    setIsViewModalOpen(true)
  }

  const handleEditForm = (formId) => {
    navigate(`/edit-form/${formId}`)
  }

  const handleDeleteForm = (form) => {
    setFormToDelete(form)
    setDeleteConfirmVisible(true)
  }

  const confirmDelete = () => {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    FormApi.deleteForm(formToDelete._id, config)
      .then(response => {
        if (response.data.status) {
          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: "Form deleted successfully"
          })
          getForms(selectedInstitution)
        }
      })
      .catch(error => {
        console.error("Error deleting form:", error)
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error.response?.data?.message || "Failed to delete form"
        })
      })
      .finally(() => {
        setDeleteConfirmVisible(false)
        setFormToDelete(null)
      })
  }

  const handleAddForm = () => {
    navigate("/addForm")
  }

  const toggleViewModal = () => {
    setIsViewModalOpen(!isViewModalOpen)
    if (!isViewModalOpen) setSelectedForm(null)
  }

  const renderFieldType = (type) => {
    const typeColors = {
      text: "primary",
      email: "info",
      password: "warning",
      number: "success",
      textarea: "secondary",
      select: "dark",
      radio: "danger",
      checkbox: "info",
      date: "primary",
      file: "warning"
    }

    return (
      <Badge color={typeColors[type] || "secondary"} className="me-1">
        {type}
      </Badge>
    )
  }

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog
        visible={deleteConfirmVisible}
        onHide={() => setDeleteConfirmVisible(false)}
        message={`Are you sure you want to delete the form "${formToDelete?.formName}"?`}
        header="Delete Confirmation"
        icon="pi pi-exclamation-triangle"
        accept={confirmDelete}
        reject={() => setDeleteConfirmVisible(false)}
      />

      {/* View Form Modal */}
      <Modal isOpen={isViewModalOpen} toggle={toggleViewModal} size="xl">
        <ModalHeader toggle={toggleViewModal}>Form Details</ModalHeader>
        <ModalBody style={{ maxHeight: '80vh', overflowY: 'auto', padding: '20px' }}>
          {selectedForm && (
            <Card>
              <CardBody>
                <div className="form-details">
                  <h5 className="mb-4">Basic Information</h5>
                  <Row className="mb-3">
                    <Col md={6}>
                      <p><strong>Form Name:</strong> {selectedForm.formName}</p>
                      <p><strong>Form Slug:</strong> {selectedForm.formSlug}</p>
                      <p><strong>Institution:</strong> {selectedForm.institution?.displayName || selectedForm.institution?.businessName}</p>
                      <p><strong>Program:</strong> {selectedForm.programId?.programName || "N/A"}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>Course:</strong> {selectedForm.courseId?.courseName || "N/A"}</p>
                      <p><strong>Created By:</strong> {selectedForm.createdBy?.userName}</p>
                      <p><strong>Created At:</strong> {new Date(selectedForm.createdAt).toLocaleDateString()}</p>
                      <p><strong>Total Fields:</strong> {selectedForm.fields?.length || 0}</p>
                    </Col>
                  </Row>

                  {selectedForm.formDescription && (
                    <>
                      <h5 className="mb-3">Description</h5>
                      <p className="mb-4">{selectedForm.formDescription}</p>
                    </>
                  )}

                  <h5 className="mb-3">Form Fields</h5>
                  <div className="fields-list">
                    {selectedForm.fields?.map((field, index) => (
                      <Card key={index} className="mb-2">
                        <CardBody className="py-2">
                          <Row>
                            <Col md={3}>
                              <strong>{field.fieldName}</strong>
                            </Col>
                            <Col md={2}>
                              {renderFieldType(field.fieldType)}
                            </Col>
                            <Col md={3}>
                              {field.fieldPlaceHolder && (
                                <small className="text-muted">Placeholder: {field.fieldPlaceHolder}</small>
                              )}
                            </Col>
                            <Col md={2}>
                              {field.sectionName && (
                                <small className="text-info">
                                  Section: {field.sectionName}
                                  <br />
                                  Priority: {field.sectionPriority || 1}
                                </small>
                              )}
                            </Col>
                            <Col md={2}>
                              {field.options && field.options.length > 0 && (
                                <small className="text-success">Options: {field.options.length}</small>
                              )}
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>
                    ))}
                  </div>
                </div>
              </CardBody>
            </Card>
          )}
        </ModalBody>
      </Modal>

      <div className="container-fluid">
        <div className="form-list-container">
          {/* Header Section */}
          <div className="form-list-header">
            <div className="header-content d-flex align-items-start justify-content-between">
              <div className="left-section">
                <CustomBreadCrumb
                  data={breadcrumbItems}
                  pageTitle="Forms Management"
                  handleClick={handleBreadcrumbClick}
                />
                <p className="text-muted mt-2 mb-0">Manage and organize your institution forms</p>
              </div>
              <div className="right-section">
                <Button
                  type="button"
                  color="primary"
                  className="waves-effect btn-modern"
                  onClick={handleAddForm}
                >
                  <i className="mdi mdi-plus me-2"></i>
                  Create New Form
                </Button>
              </div>
            </div>
          </div>

        {/* Statistics Cards */}
        {selectedInstitution && forms.length > 0 && (
          <Row className="mb-3">
            <Col xl={3} md={6}>
              <Card className="stats-card">
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="stats-icon bg-primary">
                        <i className="mdi mdi-file-document-multiple"></i>
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-2">
                      <h5 className="mb-0">{getFormStats().totalForms}</h5>
                      <p className="text-muted mb-0">Total Forms</p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xl={3} md={6}>
              <Card className="stats-card">
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="stats-icon bg-success">
                        <i className="mdi mdi-form-select"></i>
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-2">
                      <h5 className="mb-0">{getFormStats().totalFields}</h5>
                      <p className="text-muted mb-0">Total Fields</p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xl={3} md={6}>
              <Card className="stats-card">
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="stats-icon bg-info">
                        <i className="mdi mdi-chart-line"></i>
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-2">
                      <h5 className="mb-0">{getFormStats().avgFieldsPerForm}</h5>
                      <p className="text-muted mb-0">Avg Fields/Form</p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xl={3} md={6}>
              <Card className="stats-card">
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0">
                      <div className="stats-icon bg-warning">
                        <i className="mdi mdi-clock-outline"></i>
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-2">
                      <h5 className="mb-0">{getFormStats().recentForms}</h5>
                      <p className="text-muted mb-0">Recent (7 days)</p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Filters and Controls */}
        <div className="filters-container">
          <Card className="filter-card">
            <CardBody>
              <Row className="g-3 align-items-end">
                <Col lg={4} md={6} sm={12}>
                  <div className="filter-group">
                    <label className="form-label fw-semibold mb-2">
                      <i className="mdi mdi-domain me-1"></i>
                      Select Institution
                    </label>
                    <Input
                      type="select"
                      value={selectedInstitution}
                      onChange={handleInstitutionChange}
                      disabled={institutions.length === 0}
                      className="form-control-uniform institution-dropdown"
                      key={`institutions-${institutions.length}`}
                    >
                      <option value="">
                        {institutions.length > 0 ? "Choose an institution..." : "Loading institutions..."}
                      </option>
                      {institutions.map((institution) => (
                        <option key={institution.businessId} value={institution.businessId}>
                          {institution.displayName || institution.businessName || `Institution ${institution.businessId}`}
                        </option>
                      ))}
                    </Input>
                  </div>
                </Col>
              <Col lg={4} md={6} sm={12}>
                <div className="filter-group">
                  <label className="form-label fw-semibold mb-2">
                    <i className="mdi mdi-magnify me-1"></i>
                    Search Forms
                  </label>
                  <InputGroup className="input-group-uniform">
                    <InputGroupText className="input-group-text-uniform">
                      <i className="mdi mdi-magnify"></i>
                    </InputGroupText>
                    <Input
                      type="text"
                      placeholder="Search by name, program, course..."
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                      disabled={!selectedInstitution}
                      className="form-control-uniform border-start-0"
                    />
                  </InputGroup>
                </div>
              </Col>
              <Col lg={4} md={12} sm={12}>
                <div className="filter-group">
                  <label className="form-label fw-semibold mb-2">
                    <i className="mdi mdi-view-grid me-1"></i>
                    View Mode
                  </label>
                  <div className="btn-group w-100 btn-group-uniform" role="group">
                    <Button
                      color={viewMode === "cards" ? "primary" : "outline-primary"}
                      onClick={() => setViewMode("cards")}
                      className="btn-uniform flex-fill"
                    >
                      <i className="mdi mdi-view-grid me-1"></i>
                      Cards
                    </Button>
                    <Button
                      color={viewMode === "table" ? "primary" : "outline-primary"}
                      onClick={() => setViewMode("table")}
                      className="btn-uniform flex-fill"
                    >
                      <i className="mdi mdi-view-list me-1"></i>
                      Table
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
        </div>

        {/* Forms Display */}
        <div className="forms-display-container">
          {loading ? (
            <div className="text-center py-5">
              <DataTableLoader cols={3} />
            </div>
          ) : !selectedInstitution ? (
            <Card className="empty-state-card">
              <CardBody className="text-center py-5">
                <div className="empty-state-icon mb-3">
                  <i className="mdi mdi-domain text-muted"></i>
                </div>
                <h5 className="text-muted">Select an Institution</h5>
                <p className="text-muted mb-0">Choose an institution from the dropdown above to view and manage forms</p>
              </CardBody>
            </Card>
          ) : filteredForms.length === 0 ? (
            <Card className="empty-state-card">
              <CardBody className="text-center py-5">
                <div className="empty-state-icon mb-3">
                  <i className="mdi mdi-file-document-outline text-muted"></i>
                </div>
                <h5 className="text-muted">
                  {searchTerm ? "No forms match your search" : "No forms found"}
                </h5>
                <p className="text-muted mb-3">
                  {searchTerm 
                    ? "Try adjusting your search terms or clear the search to see all forms"
                    : "This institution doesn't have any forms yet. Create the first one!"
                  }
                </p>
                {!searchTerm && (
                  <Button color="primary" onClick={handleAddForm}>
                    <i className="mdi mdi-plus me-2"></i>
                    Create First Form
                  </Button>
                )}
              </CardBody>
            </Card>
          ) : viewMode === "cards" ? (
            <Row>
              {filteredForms.map((form, index) => (
                <Col xl={4} lg={6} key={form._id}>
                  <Card className="form-card h-100">
                    <CardHeader className="border-0 pb-0">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <CardTitle className="mb-1 form-title">{form.formName}</CardTitle>
                          <small className="text-muted">{form.formSlug}</small>
                        </div>
                        <div className="form-actions">
                          <ActionIcon
                            type="view"
                            onClick={() => handleViewForm(form)}
                            tooltip="View Form"
                            className="me-1"
                          />
                          <ActionIcon
                            type="edit"
                            onClick={() => handleEditForm(form._id)}
                            tooltip="Edit Form"
                            className="me-1"
                          />
                          <ActionIcon
                            type="delete"
                            onClick={() => handleDeleteForm(form)}
                            tooltip="Delete Form"
                          />
                        </div>
                      </div>
                    </CardHeader>
                    <CardBody className="pt-2">
                      <div className="form-meta mb-3">
                        <div className="meta-item">
                          <i className="mdi mdi-school text-primary me-1"></i>
                          <span className="text-muted">Program:</span>
                          <span className="ms-1">{form.programId?.programName || "N/A"}</span>
                        </div>
                        <div className="meta-item">
                          <i className="mdi mdi-book-open text-success me-1"></i>
                          <span className="text-muted">Course:</span>
                          <span className="ms-1">{form.courseId?.courseName || "N/A"}</span>
                        </div>
                      </div>
                      
                      <div className="form-stats d-flex justify-content-between align-items-center mb-3">
                        <Badge color="info" className="badge-modern">
                          <i className="mdi mdi-form-select me-1"></i>
                          {form.fields?.length || 0} fields
                        </Badge>
                        <small className="text-muted">
                          <i className="mdi mdi-calendar me-1"></i>
                          {new Date(form.createdAt).toLocaleDateString()}
                        </small>
                      </div>

                      {form.formDescription && (
                        <CardText className="text-muted form-description">
                          {form.formDescription.length > 100 
                            ? `${form.formDescription.substring(0, 100)}...` 
                            : form.formDescription
                          }
                        </CardText>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <Card>
              <CardBody className="p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="border-0">#</th>
                        <th className="border-0">Form Details</th>
                        <th className="border-0">Program</th>
                        <th className="border-0">Course</th>
                        <th className="border-0">Fields</th>
                        <th className="border-0">Created</th>
                        <th className="border-0 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredForms.map((form, index) => (
                        <tr key={form._id} className="table-row-modern">
                          <td className="fw-semibold text-muted">{index + 1}</td>
                          <td>
                            <div>
                              <h6 className="mb-1">{form.formName}</h6>
                              <small className="text-muted">{form.formSlug}</small>
                            </div>
                          </td>
                          <td>
                            <span className="text-primary">
                              {form.programId?.programName || <span className="text-muted">N/A</span>}
                            </span>
                          </td>
                          <td>
                            <span className="text-success">
                              {form.courseId?.courseName || <span className="text-muted">N/A</span>}
                            </span>
                          </td>
                          <td>
                            <Badge color="info" className="badge-modern">
                              {form.fields?.length || 0} fields
                            </Badge>
                          </td>
                          <td>
                            <small className="text-muted">
                              {new Date(form.createdAt).toLocaleDateString()}
                            </small>
                          </td>
                          <td className="text-center">
                            <div className="d-flex justify-content-center gap-1">
                              <ActionIcon
                                type="view"
                                onClick={() => handleViewForm(form)}
                                tooltip="View Form"
                              />
                              <ActionIcon
                                type="edit"
                                onClick={() => handleEditForm(form._id)}
                                tooltip="Edit Form"
                              />
                              <ActionIcon
                                type="delete"
                                onClick={() => handleDeleteForm(form)}
                                tooltip="Delete Form"
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
          )}
        </div>
        </div>
      </div>
    </>
  )
}

export default FormList